import { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { hubAPI } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import { ArrowLeft, Trophy, Skull, CalendarClock, Flame, Users2, Save, Edit3, PlayCircle, XCircle, RotateCcw, Trash2, BookOpen, Pencil, Check, X, Route, Layers } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { Hint, InfoHint } from '@/components/common/Hint';
import { TasksPanel } from '@/components/hub/TasksPanel';
import { ProjectWall } from '@/components/hub/ProjectWall';
import { PipelineEditorDialog } from '@/components/hub/PipelineEditorDialog';

const importanceColor = (n) => {
    if (n >= 8) return 'bg-red-500';
    if (n >= 5) return 'bg-amber-500';
    if (n >= 3) return 'bg-blue-500';
    return 'bg-slate-400';
};

const daysSince = (iso) => {
    if (!iso) return null;
    const d = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
    return Number.isFinite(d) ? d : null;
};

export default function HubProjectDetail() {
    const { projectId } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const [project, setProject] = useState(null);
    const [theme, setTheme] = useState(null);
    const [members, setMembers] = useState([]);
    const [access, setAccess] = useState(null);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(false);
    const [closingDialog, setClosingDialog] = useState(null); // 'won' | 'lost' | null
    const [closeComment, setCloseComment] = useState('');
    const [saving, setSaving] = useState(false);
    const [form, setForm] = useState({});
    const [pipelineEditorOpen, setPipelineEditorOpen] = useState(false);
    const [allThemes, setAllThemes] = useState([]);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [pR, tR, mR, aR] = await Promise.all([
                hubAPI.getProject(projectId),
                hubAPI.listThemes(),
                hubAPI.members().catch(() => ({ data: [] })),
                hubAPI.access(),
            ]);
            setProject(pR.data);
            setAllThemes(tR.data || []);
            setTheme((tR.data || []).find((t) => t.id === pR.data.theme_id));
            setMembers(mR.data || []);
            setAccess(aR.data);
            setForm({
                name: pR.data.name,
                description: pR.data.description || '',
                goal: pR.data.goal || '',
                importance: pR.data.importance,
                deadline: (pR.data.deadline || '').slice(0, 10),
                members: pR.data.members || [],
                theme_id: pR.data.theme_id,
            });
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Projet introuvable');
        } finally {
            setLoading(false);
        }
    }, [projectId]);

    useEffect(() => { load(); }, [load]);

    // Marquer le projet comme "visité" à l'entrée (pour effacer l'indicateur non-lus du kanban
    // ET faire redescendre la pastille rouge du menu "Projets" instantanément).
    useEffect(() => {
        if (projectId) {
            hubAPI.markProjectVisited(projectId)
                .then(() => {
                    // Signal aux badges globaux (Sidebar / MobileNav) qu'ils doivent se rafraîchir
                    try { window.dispatchEvent(new Event('hub:activity-seen')); } catch { /* noop */ }
                })
                .catch(() => {/* silencieux */});
        }
    }, [projectId]);

    const isOwner = project && user && project.owner_id === user.id;
    const isAdmin = access?.is_admin;
    const isProjectMember = !!(project && user && (project.members || []).includes(user.id));
    // canEdit = droit d'édition au niveau de la fiche (statut, membres, étape…) — hub_access full ou admin.
    const canEdit = access?.access === 'full' || isAdmin;
    // canParticipate = poster sur le mur, créer/modifier des tâches — nécessite d'être MEMBRE du projet
    // (ou owner, ou admin qui a un bypass automatique).
    const canParticipate = isAdmin || isOwner || isProjectMember;
    const isClosed = project?.status === 'won' || project?.status === 'lost';

    const moveStage = async (stageId) => {
        try {
            const r = await hubAPI.moveStage(projectId, stageId);
            setProject(r.data);
            const carried = r.data?._tasks_carried_over || 0;
            if (carried > 0) {
                toast.success(
                    `Étape mise à jour — ${carried} tâche${carried > 1 ? 's' : ''} non terminée${carried > 1 ? 's' : ''} report${carried > 1 ? 'ées' : 'ée'} sur la nouvelle étape`,
                    { duration: 5000 }
                );
            } else {
                toast.success('Étape mise à jour');
            }
            // Recharge les tâches pour refléter la migration
            load?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Erreur');
        }
    };

    const submitEdit = async () => {
        setSaving(true);
        try {
            const payload = { ...form };
            if (payload.deadline) payload.deadline = new Date(payload.deadline).toISOString();
            const r = await hubAPI.updateProject(projectId, payload);
            setProject(r.data);
            setEditing(false);
            toast.success('Projet mis à jour');
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Erreur');
        } finally {
            setSaving(false);
        }
    };

    const submitClose = async () => {
        if (!closeComment.trim()) { toast.error('Commentaire obligatoire'); return; }
        try {
            const r = await hubAPI.closeProject(projectId, closingDialog, closeComment.trim());
            setProject(r.data);
            setClosingDialog(null);
            setCloseComment('');
            toast.success(`Projet ${closingDialog === 'won' ? 'gagné' : 'clôturé'} 🎉`);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Erreur');
        }
    };

    const reopen = async () => {
        try {
            const r = await hubAPI.reopenProject(projectId);
            setProject(r.data);
            toast.success('Projet rouvert');
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Erreur');
        }
    };

    const deleteProject = async () => {
        if (!window.confirm('Supprimer définitivement ce projet ?')) return;
        try {
            await hubAPI.deleteProject(projectId);
            toast.success('Projet supprimé');
            navigate(`/projets/espace/${project.space_id}`);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Erreur');
        }
    };

    if (loading) return <DashboardLayout><div className="p-6 text-sm text-slate-500" data-testid="hp-loading">Chargement…</div></DashboardLayout>;
    if (!project) return <DashboardLayout><div className="p-6 text-sm text-slate-500">Projet introuvable.</div></DashboardLayout>;

    const days = project.deadline ? Math.ceil((new Date(project.deadline).getTime() - Date.now()) / 86400000) : null;
    // Utilise le pipeline PROPRE au projet (peut être personnalisé), fallback sur le thème.
    const activePipeline = (project.pipeline && project.pipeline.length ? project.pipeline : theme?.pipeline) || [];
    const currentIndex = activePipeline.findIndex((s) => s.id === project.current_stage_id) ?? -1;
    const currentStage = activePipeline[currentIndex];
    const daysOnStage = daysSince(project.stage_changed_at || project.created_at);
    const stageMax = currentStage?.max_days || null;
    const stageOver = stageMax && daysOnStage != null ? Math.max(0, daysOnStage - stageMax) : 0;
    const stageLeft = stageMax && daysOnStage != null ? (stageMax - daysOnStage) : null;
    const stageBlocked = stageOver > 0 && !isClosed;

    return (
        <DashboardLayout>
        <div className="max-w-5xl mx-auto p-3 sm:p-6 space-y-5" data-testid="hub-project-detail">
            <button onClick={() => navigate(`/projets/espace/${project.space_id}`)} className="text-xs text-slate-500 hover:text-slate-800 flex items-center gap-1" data-testid="btn-back-space">
                <ArrowLeft className="w-3.5 h-3.5" /> Retour à l&apos;espace
            </button>

            {stageBlocked && (
                <div className="rounded-lg border-2 border-red-300 bg-red-50 p-3 flex items-start gap-3" data-testid="stage-blocked-alert">
                    <div className="w-8 h-8 rounded-full bg-red-500 text-white flex items-center justify-center shrink-0">
                        ⚠
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold text-red-800">
                            Projet en retard : {stageOver} j au-delà du seuil de {stageMax} j sur l&apos;étape actuelle (sur cette étape depuis {daysOnStage} j).
                        </div>
                        <div className="text-xs text-red-700 mt-0.5">
                            L&apos;étape « {currentStage?.name} » ne doit pas dépasser {stageMax} j. Faites avancer le projet ou ajustez la durée max de l&apos;étape.
                        </div>
                    </div>
                </div>
            )}

            {/* Header */}
            <Card>
                <CardContent className="p-5 space-y-3">
                    <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
                        <div className="flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                                {theme && <Badge style={{ backgroundColor: `${theme.color}20`, color: theme.color, borderColor: 'transparent' }} className="border">
                                    <span className="w-1.5 h-1.5 rounded-full mr-1.5 inline-block" style={{ backgroundColor: theme.color }} /> {theme.name}
                                </Badge>}
                                {project.status === 'won' && <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 gap-1"><Trophy className="w-3 h-3" /> Gagné</Badge>}
                                {project.status === 'lost' && <Badge className="bg-slate-200 text-slate-700 hover:bg-slate-200 gap-1"><Skull className="w-3 h-3" /> Clôturé</Badge>}
                            </div>
                            <h1 className="text-2xl font-bold text-slate-900 mt-2" data-testid="hp-name">{project.name}</h1>
                            {project.goal && (
                                <p className="text-sm text-slate-600 mt-2 italic border-l-2 border-indigo-300 pl-3" data-testid="hp-goal">
                                    🎯 <span className="font-medium not-italic">But :</span> {project.goal}
                                </p>
                            )}
                        </div>
                        <div className="flex flex-wrap gap-2">
                            {!isClosed && canEdit && (
                                <>
                                    <Hint text="Modifier le nom, la deadline, l'importance, le but ou la description de ce projet.">
                                        <Button variant="outline" size="sm" onClick={() => setEditing(true)} className="gap-1.5" data-testid="btn-edit">
                                            <Edit3 className="w-4 h-4" /> Modifier
                                        </Button>
                                    </Hint>
                                    <Hint text="Le projet a atteint son but : marquez-le comme gagné. Un commentaire sera demandé pour capitaliser sur l'expérience.">
                                        <Button size="sm" onClick={() => setClosingDialog('won')} className="gap-1.5 bg-emerald-600 hover:bg-emerald-700" data-testid="btn-won">
                                            <Trophy className="w-4 h-4" /> Gagné
                                        </Button>
                                    </Hint>
                                    <Hint text="Clôturer sans succès (abandon, mauvais timing, impossible…). Commentaire OBLIGATOIRE — c'est ce qui vous fera progresser.">
                                        <Button size="sm" variant="outline" onClick={() => setClosingDialog('lost')} className="gap-1.5 text-slate-700" data-testid="btn-lost">
                                            <XCircle className="w-4 h-4" /> Clôturer
                                        </Button>
                                    </Hint>
                                </>
                            )}
                            {isClosed && (isOwner || isAdmin) && (
                                <Hint text="Ré-ouvrir ce projet pour qu'il retourne dans le kanban actif.">
                                    <Button variant="outline" size="sm" onClick={reopen} className="gap-1.5" data-testid="btn-reopen">
                                        <RotateCcw className="w-4 h-4" /> Rouvrir
                                    </Button>
                                </Hint>
                            )}
                            {(isOwner || isAdmin) && (
                                <Hint text="Supprimer définitivement ce projet. Action irréversible.">
                                    <Button variant="outline" size="sm" onClick={deleteProject} className="gap-1.5 text-red-600 hover:text-red-700" data-testid="btn-delete">
                                        <Trash2 className="w-4 h-4" />
                                    </Button>
                                </Hint>
                            )}
                        </div>
                    </div>

                    {/* Stats */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-3 border-t">
                        <StatItem icon={Flame} label="Importance" value={`${project.importance}/10`} color={importanceColor(project.importance)} hint="Priorité relative du projet (0 basse → 10 critique). Sert au tri et au code couleur des cartes." />
                        <StatItem
                            icon={CalendarClock}
                            label="Date butoir"
                            hint="Deadline informative avant laquelle le projet devrait être conclu. Ne déclenche pas d'alerte « en retard » — c'est la durée d'étape qui fait foi (voir « Étape actuelle »)."
                            value={project.deadline ? new Date(project.deadline).toLocaleDateString('fr-FR') : '—'}
                            sub={days != null && !isClosed ? (days < 0 ? `Deadline passée +${Math.abs(days)}j` : `J-${days}`) : null}
                            subColor={days != null && days < 0 ? 'text-slate-500' : days <= 3 ? 'text-amber-600' : 'text-slate-500'}
                        />
                        <StatItem icon={Users2} label="Membres" value={project.members?.length || 0} hint="Utilisateurs impliqués sur ce projet (indépendants des équipes CRM). Ils peuvent le voir dans leur liste « Mes projets »." />
                        <StatItem
                            icon={PlayCircle}
                            label="Étape actuelle"
                            hint="Position dans le pipeline du thème. Cliquez sur une étape en dessous pour la faire avancer. Chaque étape a un seuil de temps (max_days) — au-delà, un badge d'alerte apparaît."
                            value={currentStage?.name || '—'}
                            sub={stageMax ? (
                                stageBlocked
                                    ? `⚠ En retard : +${stageOver}j (max ${stageMax}j)`
                                    : (daysOnStage != null ? `Sur cette étape depuis ${daysOnStage}j / max ${stageMax}j` : null)
                            ) : (daysOnStage != null ? `Sur cette étape depuis ${daysOnStage}j (pas de seuil défini)` : null)}
                            subColor={stageBlocked ? 'text-red-600' : (stageLeft != null && stageLeft <= 2 ? 'text-amber-600' : 'text-slate-500')}
                        />
                    </div>

                    {isClosed && project.outcome_comment && (
                        <div className={cn("pt-3 border-t rounded-lg p-3 mt-2", project.status === 'won' ? 'bg-emerald-50' : 'bg-slate-50')}>
                            <div className="text-xs font-semibold text-slate-700 mb-1">
                                {project.status === 'won' ? '🏆 Commentaire de clôture — Gagné' : '📝 Commentaire de clôture'}
                            </div>
                            <p className="text-sm text-slate-700 whitespace-pre-wrap" data-testid="hp-outcome">{project.outcome_comment}</p>
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Members */}
            <Card>
                <CardHeader className="pb-2">
                    <CardTitle className="text-sm text-slate-700 flex items-center gap-2"><Users2 className="w-4 h-4" /> Membres du projet</CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="flex flex-wrap gap-2">
                        {(project.members || []).map((mid) => {
                            const m = members.find((mm) => mm.id === mid);
                            if (!m) return <Badge key={mid} variant="secondary" className="text-[10px]">?</Badge>;
                            return (
                                <div key={mid} className="flex items-center gap-2 rounded-full border border-slate-200 bg-white px-2.5 py-1">
                                    <span className="w-5 h-5 rounded-full bg-indigo-100 text-indigo-700 text-[10px] flex items-center justify-center font-semibold">
                                        {(m.first_name?.[0] || '?')}{(m.last_name?.[0] || '')}
                                    </span>
                                    <span className="text-xs text-slate-700">{m.first_name} {m.last_name}</span>
                                    {mid === project.owner_id && <span className="text-[9px] text-indigo-600 font-semibold">OWNER</span>}
                                </div>
                            );
                        })}
                    </div>
                </CardContent>
            </Card>

            {/* Pipeline */}
            {(theme || activePipeline.length > 0) && (
                <Card data-testid="hp-pipeline">
                    <CardHeader className="pb-2">
                        <CardTitle className="text-sm text-slate-700 flex items-center justify-between gap-2">
                            <div className="flex items-center gap-2">
                                <PlayCircle className="w-4 h-4" /> Pipeline
                                {theme && <span className="text-slate-500 font-normal">— {theme.name}</span>}
                                {project.pipeline && project.pipeline.length > 0 && (
                                    <Badge className="bg-indigo-50 text-indigo-700 hover:bg-indigo-50 text-[10px]">Personnalisé</Badge>
                                )}
                                <InfoHint text="Cliquez sur une étape pour la définir comme étape actuelle. Le pipeline peut être personnalisé pour ce projet (nombre d'étapes, noms, délais) — indépendamment du thème." />
                            </div>
                            {canEdit && !isClosed && (
                                <Hint text="Personnaliser le pipeline de ce projet (nombre d'étapes, noms, couleurs, délais max par étape). Le thème sert de modèle initial mais chaque projet peut avoir le sien.">
                                    <Button size="sm" variant="outline" onClick={() => setPipelineEditorOpen(true)} className="gap-1 text-xs h-7" data-testid="btn-edit-project-pipeline">
                                        <Route className="w-3.5 h-3.5" /> Éditer le pipeline
                                    </Button>
                                </Hint>
                            )}
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <p className="text-[11px] text-slate-400 mb-3">Cliquez sur une étape pour la définir comme étape actuelle.</p>
                        <div className="flex items-stretch gap-0 overflow-x-auto pb-2">
                            {activePipeline.map((s, i) => {
                                const done = i < currentIndex;
                                const active = s.id === project.current_stage_id;
                                return (
                                    <Hint
                                        key={s.id}
                                        text={active
                                            ? `Étape actuelle : ${s.name}${s.max_days ? ` — durée max : ${s.max_days}j` : ''}`
                                            : done
                                                ? `Étape déjà validée : ${s.name}`
                                                : `Cliquez pour passer à : ${s.name}${s.max_days ? ` (durée max : ${s.max_days}j)` : ''}`
                                        }
                                    >
                                        <button
                                            onClick={() => !isClosed && canEdit && moveStage(s.id)}
                                            disabled={isClosed || !canEdit}
                                            data-testid={`hp-stage-${s.id}`}
                                            className={cn(
                                                "flex-1 min-w-[110px] flex flex-col items-center gap-1 px-2 py-3 border-b-2 transition-all",
                                                active && stageBlocked && "border-b-red-500 bg-red-50/50",
                                                active && !stageBlocked && "border-b-indigo-500 bg-indigo-50/50",
                                                done && !active && "border-b-emerald-500 opacity-80",
                                                !active && !done && "border-b-slate-200",
                                                !isClosed && canEdit && !active && "hover:bg-slate-50 cursor-pointer",
                                                (isClosed || !canEdit) && "cursor-not-allowed opacity-70"
                                            )}
                                        >
                                            <div className={cn(
                                                "w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold text-white",
                                                active && stageBlocked ? "bg-red-500 ring-4 ring-red-100" :
                                                active ? "bg-indigo-500 ring-4 ring-indigo-100" :
                                                done ? "bg-emerald-500" : "bg-slate-300"
                                            )} style={active && !stageBlocked ? { backgroundColor: s.color } : {}}>
                                                {i + 1}
                                            </div>
                                            <span className={cn("text-[11px] text-center leading-tight", active ? "font-semibold text-slate-900" : "text-slate-600")}>{s.name}</span>
                                            {s.max_days && (
                                                <span className={cn("text-[9px]", active && stageBlocked ? "text-red-600 font-semibold" : "text-slate-400")}>
                                                    max {s.max_days}j
                                                </span>
                                            )}
                                        </button>
                                    </Hint>
                                );
                            })}
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Bandeau contexte & règles du projet (multiligne, éditable owner/admin) */}
            <ProjectDescriptionCard
                project={project}
                canEdit={canEdit}
                onSaved={(updated) => setProject(updated)}
            />

            {/* Panel Tâches */}
            <TasksPanel project={project} theme={theme} members={members} canWrite={canParticipate && !isClosed} />

            {/* Mur d'échange du projet (Phase 2c) */}
            <ProjectWall key={projectId} projectId={projectId} canWrite={canParticipate && !isClosed} project={project} allMembers={members} />

            {/* Dialog Edit */}
            <Dialog open={editing} onOpenChange={setEditing}>
                <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto" data-testid="hp-edit-dialog">
                    <DialogHeader>
                        <DialogTitle>Modifier le projet</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div>
                            <Label>Nom</Label>
                            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="hp-edit-name" />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label>Date butoir</Label>
                                <Input type="date" value={form.deadline} onChange={(e) => setForm({ ...form, deadline: e.target.value })} data-testid="hp-edit-deadline" />
                                <DeadlineCoherence
                                    formDeadline={form.deadline}
                                    project={project}
                                    activePipeline={activePipeline}
                                    onAlignPipeline={async (newPipeline) => {
                                        try {
                                            const r = await hubAPI.updateProject(projectId, { pipeline: newPipeline });
                                            setProject(r.data);
                                            toast.success('Pipeline aligné sur la date butoir');
                                        } catch (e) {
                                            toast.error(e.response?.data?.detail || 'Alignement impossible');
                                        }
                                    }}
                                />
                            </div>
                            <div>
                                <Label>Importance : <span className="font-semibold text-indigo-600">{form.importance}/10</span></Label>
                                <input type="range" min="0" max="10" value={form.importance} onChange={(e) => setForm({ ...form, importance: parseInt(e.target.value) })} className="w-full mt-2" data-testid="hp-edit-imp" />
                            </div>
                        </div>
                        <div>
                            <Label>But final</Label>
                            <Textarea rows={2} value={form.goal} onChange={(e) => setForm({ ...form, goal: e.target.value })} data-testid="hp-edit-goal" />
                        </div>
                        <div>
                            <Label>Description</Label>
                            <Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="hp-edit-desc" />
                        </div>
                        {/* Changement de thème (admin/full) — remplace le pipeline courant par celui du nouveau thème */}
                        {canEdit && (
                            <div>
                                <Label className="flex items-center gap-1.5">
                                    <Layers className="w-3.5 h-3.5 text-indigo-600" /> Thème
                                    <InfoHint text="Changer de thème remplace le pipeline actuel par celui du nouveau thème. Attention : les étapes personnalisées de ce projet seront perdues, et les tâches non-liées à la nouvelle 1ère étape y seront rapatriées." />
                                </Label>
                                <select
                                    className="mt-1 w-full h-9 rounded-md border border-slate-200 px-2 text-sm bg-white"
                                    value={form.theme_id || ''}
                                    onChange={(e) => setForm({ ...form, theme_id: e.target.value })}
                                    data-testid="hp-edit-theme"
                                >
                                    {allThemes.map((t) => (
                                        <option key={t.id} value={t.id}>
                                            {t.icon || '📁'} {t.name}
                                        </option>
                                    ))}
                                </select>
                                {form.theme_id !== project.theme_id && (
                                    <div className="mt-1.5 text-[11px] text-amber-700 bg-amber-50 border border-amber-100 rounded px-2 py-1.5 flex items-start gap-1.5">
                                        <span className="text-sm">⚠️</span>
                                        <span>En changeant de thème, le pipeline sera réinitialisé au modèle du nouveau thème.</span>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setEditing(false)}>Annuler</Button>
                        <Button onClick={submitEdit} disabled={saving} data-testid="hp-edit-save"><Save className="w-4 h-4 mr-1" /> {saving ? '…' : 'Enregistrer'}</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Dialog éditeur de pipeline propre au projet */}
            {pipelineEditorOpen && (
                <PipelineEditorDialog
                    open={pipelineEditorOpen}
                    onOpenChange={setPipelineEditorOpen}
                    project={project}
                    theme={theme}
                    onSaved={(updated) => {
                        setProject(updated);
                        setPipelineEditorOpen(false);
                        toast.success('Pipeline du projet mis à jour');
                    }}
                />
            )}

            {/* Dialog Close */}
            <Dialog open={!!closingDialog} onOpenChange={(o) => { if (!o) setClosingDialog(null); }}>
                <DialogContent data-testid="hp-close-dialog">
                    <DialogHeader>
                        <DialogTitle>{closingDialog === 'won' ? '🏆 Marquer comme gagné' : '📝 Clôturer le projet'}</DialogTitle>
                        <DialogDescription>
                            {closingDialog === 'won'
                                ? "Bravo ! Un mot pour expliquer si le résultat diffère du but initial (obligatoire)."
                                : "Un projet doit toujours être conclu. Expliquez pourquoi il est clôturé (obligatoire)."}
                        </DialogDescription>
                    </DialogHeader>
                    <Textarea
                        rows={4}
                        placeholder="Ex : Objectif atteint à 90%, léger dépassement du délai, écarts principaux…"
                        value={closeComment}
                        onChange={(e) => setCloseComment(e.target.value)}
                        data-testid="hp-close-comment"
                    />
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setClosingDialog(null)}>Annuler</Button>
                        <Button
                            onClick={submitClose}
                            className={closingDialog === 'won' ? 'bg-emerald-600 hover:bg-emerald-700' : ''}
                            data-testid="hp-close-submit"
                        >
                            {closingDialog === 'won' ? 'Valider — Gagné' : 'Valider — Clôturer'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
        </DashboardLayout>
    );
}

function StatItem({ icon: Icon, label, value, color, sub, subColor, hint }) {
    return (
        <div className="rounded-lg bg-slate-50 p-3">
            <div className="text-[10px] text-slate-500 uppercase tracking-wider flex items-center gap-1">
                <Icon className="w-3 h-3" /> {label}
                {hint && <InfoHint text={hint} />}
            </div>
            <div className="text-sm font-semibold text-slate-900 mt-0.5 flex items-center gap-2">
                {color && <span className={cn("w-2 h-2 rounded-full", color)} />}
                {value}
            </div>
            {sub && <div className={cn("text-[11px] mt-0.5 font-medium", subColor || 'text-slate-500')}>{sub}</div>}
        </div>
    );
}

// Encart contexte / description du projet — visible par tous, éditable par owner + admin
function ProjectDescriptionCard({ project, canEdit, onSaved }) {
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(project?.description || '');
    const [saving, setSaving] = useState(false);

    useEffect(() => { setDraft(project?.description || ''); }, [project?.id, project?.description]);

    const submit = async () => {
        setSaving(true);
        try {
            const r = await hubAPI.updateProject(project.id, { description: draft });
            toast.success('Contexte du projet mis à jour');
            onSaved?.(r.data);
            setEditing(false);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Enregistrement impossible');
        } finally {
            setSaving(false);
        }
    };

    const hasContent = (project?.description || '').trim().length > 0;
    if (!hasContent && !canEdit) return null;

    return (
        <div
            className="rounded-xl border border-slate-200 bg-gradient-to-br from-slate-50 to-white p-4"
            data-testid="project-description-card"
        >
            <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center shrink-0">
                    <BookOpen className="w-4 h-4 text-slate-600" />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2 mb-1">
                        <h3 className="text-sm font-semibold text-slate-800">Contexte du projet</h3>
                        {canEdit && !editing && (
                            <button
                                onClick={() => setEditing(true)}
                                className="text-[11px] text-indigo-600 hover:text-indigo-800 flex items-center gap-1"
                                data-testid="hp-desc-edit"
                            >
                                <Pencil className="w-3 h-3" /> {hasContent ? 'Modifier' : 'Ajouter du contexte'}
                            </button>
                        )}
                    </div>
                    {editing ? (
                        <div className="space-y-2" data-testid="hp-desc-edit-form">
                            <Textarea
                                autoFocus
                                value={draft}
                                onChange={(e) => setDraft(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Escape') { setEditing(false); setDraft(project?.description || ''); }
                                    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) { e.preventDefault(); submit(); }
                                }}
                                rows={6}
                                placeholder={"Ex: Le client X nous a sollicités pour valider la fiche IND-UT-116 sur son site de Y.\n\nContexte :\n- Consommation actuelle : ...\n- Enjeux réglementaires : ...\n- Partenaires impliqués : ...\n\nHistorique :\n- Contact initial : mars 2026\n- Étude préalable envoyée : ..."}
                                className="text-sm resize-y"
                                data-testid="hp-desc-textarea"
                            />
                            <div className="flex items-center gap-2 text-[11px]">
                                <Button size="sm" onClick={submit} disabled={saving} className="gap-1" data-testid="hp-desc-save">
                                    <Check className="w-3.5 h-3.5" /> {saving ? '…' : 'Enregistrer'}
                                </Button>
                                <Button size="sm" variant="ghost" onClick={() => { setEditing(false); setDraft(project?.description || ''); }} disabled={saving} className="gap-1">
                                    <X className="w-3.5 h-3.5" /> Annuler
                                </Button>
                                <span className="text-slate-400 ml-auto hidden sm:inline">Ctrl+Entrée = valider · Échap = annuler</span>
                            </div>
                        </div>
                    ) : hasContent ? (
                        <p className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed" data-testid="hp-desc-text">
                            {project.description}
                        </p>
                    ) : (
                        <p className="text-xs text-slate-400 italic">Aucun contexte pour ce projet. Ajoutez-en pour que toute l&apos;équipe ait la même compréhension du sujet, des enjeux et de l&apos;historique.</p>
                    )}
                </div>
            </div>
        </div>
    );
}



// Encart de cohérence entre la date butoir et la somme des étapes du pipeline.
// Rescale automatiquement le pipeline proportionnellement quand la deadline est changée.
function DeadlineCoherence({ formDeadline, project, activePipeline, onAlignPipeline }) {
    if (!formDeadline || !project?.created_at || !activePipeline?.length) return null;
    const start = new Date(project.created_at);
    const deadline = new Date(formDeadline);
    const totalAvailableDays = Math.max(1, Math.round((deadline.getTime() - start.getTime()) / 86400000));
    const totalPipelineDays = activePipeline.reduce((n, s) => n + (Number(s.max_days) || 0), 0);
    if (totalPipelineDays === 0) {
        return (
            <div className="mt-1 text-[11px] text-slate-500" data-testid="deadline-coherence">
                Aucune durée max définie dans le pipeline.
            </div>
        );
    }
    const diff = totalAvailableDays - totalPipelineDays;
    const coherent = Math.abs(diff) <= 1;

    // Reproportionne toutes les étapes proportionnellement au nouveau total
    const rescalePipeline = () => {
        const ratio = totalAvailableDays / totalPipelineDays;
        // Ajustement des étapes ayant un max_days
        const stagesWithDays = activePipeline.map((s) => ({
            id: s.id,
            name: s.name,
            color: s.color,
            max_days: s.max_days != null ? Math.max(1, Math.round(Number(s.max_days) * ratio)) : null,
        }));
        // Corriger l'arrondi : ajuster la dernière étape avec max_days pour tomber pile
        const currentSum = stagesWithDays.reduce((n, s) => n + (Number(s.max_days) || 0), 0);
        const delta = totalAvailableDays - currentSum;
        if (delta !== 0) {
            for (let i = stagesWithDays.length - 1; i >= 0; i--) {
                if (stagesWithDays[i].max_days != null) {
                    stagesWithDays[i].max_days = Math.max(1, stagesWithDays[i].max_days + delta);
                    break;
                }
            }
        }
        const newPipeline = stagesWithDays.map((s, i) => ({ ...s, order: i + 1 }));
        onAlignPipeline(newPipeline);
    };

    return (
        <div className="mt-1 text-[11px] space-y-1" data-testid="deadline-coherence">
            <div className={cn('flex items-center gap-1.5', coherent ? 'text-emerald-700' : 'text-amber-700')}>
                {coherent ? <span>✓</span> : <span>⚠️</span>}
                <span>
                    Deadline : <b>{totalAvailableDays}j</b> · Pipeline : <b>{totalPipelineDays}j</b>
                    {!coherent && (diff > 0 ? ` · ${diff}j non affectés` : ` · ${Math.abs(diff)}j en trop`)}
                </span>
            </div>
            {!coherent && (
                <button
                    type="button"
                    onClick={rescalePipeline}
                    className="text-[11px] text-indigo-600 hover:text-indigo-800 underline"
                    data-testid="btn-align-pipeline"
                >
                    Réajuster le pipeline proportionnellement ({diff > 0 ? '+' : ''}{diff}j répartis sur les étapes)
                </button>
            )}
        </div>
    );
}
